/**
 * §19 ТЗ: интерфейс русский.
 *
 * Подписи значений enum'ов живут в контрактах: тот же словарь берёт сервер
 * для выгрузки CSV (§14, E9), поэтому статус в таблице и в файле называется
 * одинаково. Здесь остаётся только то, что нужно исключительно интерфейсу.
 */
// Реэкспорт не создаёт локальной привязки, а словарь ролей нужен и здесь —
// в расшифровке значений аудита.
import { ROLE_LABEL as ROLE_LABEL_SOURCE } from '@blesk/contracts';

export {
  ROLE_LABEL,
  PROJECT_STATUS_LABEL,
  EMPLOYEE_STATUS_LABEL,
  PERSON_STATUS_LABEL,
  LINK_STATUS_LABEL,
  INVITE_STATUS_LABEL,
  CHANNEL_LABEL,
  TEMPLATE_STATUS_LABEL,
  SCHEDULE_STATUS_LABEL,
  RUN_STATUS_LABEL,
  INCIDENT_STATUS_LABEL,
  REQUEST_STATUS_LABEL,
  REQUEST_TYPE_LABEL,
  OUTBOX_STATUS_LABEL,
  PROJECTION_KIND_LABEL,
} from '@blesk/contracts';

export const OUTBOX_COMMAND_LABEL: Record<string, string> = {
  SEND_MESSAGE: 'Отправка сообщения',
  UPSERT_CONTACT: 'Проекция контакта',
  UPSERT_LIST_ROW: 'Проекция строки списка',
  CREATE_TASK: 'Создание задачи',
};

/**
 * §15.1, T40. Статус приложения в каталоге портала — сказанный тем, что он
 * означает для человека. «PRIVATE» само по себе не объясняет, почему сотрудник
 * видит «Нет доступа», а именно за этим в раздел и приходят.
 */
export const CATALOG_STATUS_LABEL: Record<string, string> = {
  PRIVATE: 'видно только автору приложения',
  PUBLISHED: 'видно всем сотрудникам портала',
  UNPUBLISHED: 'видно всем сотрудникам, приложение выключено',
};

/** Действия аудита. Неизвестное действие показывается как есть — это не ошибка. */
export const AUDIT_ACTION_LABEL: Record<string, string> = {
  LOGIN_SUCCEEDED: 'Вход выполнен',
  LOGIN_FAILED: 'Неудачный вход',
  LOGOUT: 'Выход',
  PASSWORD_CHANGED: 'Пароль изменён',
  PASSWORD_RESET: 'Пароль сброшен',
  ROLE_CHANGED: 'Роль изменена',
  EMPLOYEE_CREATED: 'Сотрудник создан',
  EMPLOYEE_BLOCKED: 'Сотрудник заблокирован',
  EMPLOYEE_UNBLOCKED: 'Сотрудник разблокирован',
  EMPLOYEE_BITRIX_LINKED: 'Сотрудник сопоставлен с Bitrix24',
  PROJECT_CREATED: 'Объект создан',
  PROJECT_UPDATED: 'Объект изменён',
  PROJECT_ARCHIVED: 'Объект архивирован',
  PROJECT_SYNCED: 'Объект синхронизирован',
  MANAGER_CHANGED: 'Менеджер изменён',
  OBSERVERS_CHANGED: 'Наблюдатели изменены',
  INVITE_CREATED: 'Приглашение создано',
  INVITE_REVOKED: 'Приглашение отозвано',
  INVITE_USED: 'Приглашение использовано',
  INVITE_REJECTED: 'Приглашение отклонено',
  PERSON_REGISTERED: 'Администратор зарегистрирован',
  PERSON_MERGED: 'Персоны слиты',
  PERSON_BLOCKED: 'Администратор заблокирован',
  LINK_REVOKED: 'Связь отозвана',
  TEMPLATE_PUBLISHED: 'Версия анкеты опубликована',
  TEMPLATE_ARCHIVED: 'Анкета архивирована',
  TEMPLATE_RESTORED: 'Анкета возвращена из архива',
  SCHEDULE_CREATED: 'Расписание создано',
  SCHEDULE_UPDATED: 'Расписание изменено',
  SCHEDULE_STATUS_CHANGED: 'Статус расписания изменён',
  INCIDENT_CREATED: 'Инцидент создан',
  INCIDENT_STATUS_CHANGED: 'Статус инцидента изменён',
  REQUEST_CREATED: 'Обращение создано',
  REQUEST_STATUS_CHANGED: 'Статус обращения изменён',
  REQUEST_ACCESS_DENIED: 'Отказ по чужому объекту',
  OUTBOX_RETRIED: 'Ручной повтор операции',
  OUTBOX_RESOLVED: 'Операция сверена',
  OUTBOX_ENQUEUED: 'Команда поставлена вручную',
  SETTING_CHANGED: 'Настройка изменена',
  B24_UNAVAILABLE: 'Bitrix24 недоступен',
  B24_RESTORED: 'Bitrix24 восстановлен',
};

export function auditActionLabel(action: string): string {
  return AUDIT_ACTION_LABEL[action] ?? action;
}

/**
 * Кнопка называется действием, а не тем, что получится. «Закрыт» на кнопке
 * читается как состояние записи, «Закрыть» — как то, что произойдёт по нажатию;
 * статусы остаются словарями таблиц и выгрузок.
 */
export const INCIDENT_ACTION_LABEL: Record<'IN_PROGRESS' | 'CLOSED' | 'REJECTED', string> = {
  IN_PROGRESS: 'Взять в работу',
  CLOSED: 'Закрыть',
  REJECTED: 'Отклонить как ложный',
};

export const REQUEST_ACTION_LABEL: Record<
  'ACCEPTED' | 'IN_PROGRESS' | 'CLOSED' | 'REJECTED',
  string
> = {
  ACCEPTED: 'Принять',
  IN_PROGRESS: 'Взять в работу',
  CLOSED: 'Закрыть',
  REJECTED: 'Отклонить',
};

/**
 * §18: состав изменений в аудите зависит от действия, но ключи повторяются
 * из события в событие. Известные переводятся, неизвестные показываются как
 * есть — это не ошибка, а новое поле, которое ещё не описали.
 */
const AUDIT_FIELD_LABEL: Record<string, string> = {
  from: 'было',
  to: 'стало',
  fromStatus: 'было',
  status: 'статус',
  role: 'роль',
  name: 'название',
  fullName: 'ФИО',
  projectId: 'объект',
  personId: 'администратор',
  employeeId: 'сотрудник',
  managerId: 'менеджер',
  sourcePersonId: 'персона-источник',
  projectAdminId: 'связь',
  number: 'номер',
  version: 'версия',
  type: 'тип',
  channel: 'канал',
  reason: 'причина',
  source: 'источник',
  scope: 'область',
  tag: 'тег группы',
  timezone: 'часовой пояс',
  localTime: 'время запуска',
  rule: 'правило',
  negativeRule: 'правило негатива',
  questions: 'вопросов',
  violations: 'нарушений',
  attempts: 'попыток',
  expiresAt: 'действует до',
  syncedAt: 'синхронизировано',
  textLength: 'длина текста',
  command: 'команда',
  decision: 'решение',
  note: 'пояснение',
  externalId: 'внешний ключ',
  bitrixGroupId: 'группа Bitrix24',
  bitrixUserId: 'пользователь Bitrix24',
  bitrixMissing: 'нет в Bitrix24',
  mustChangePassword: 'смена пароля обязательна',
  revokedInvites: 'отозвано приглашений',
  revokedLinks: 'отозвано связей',
  movedLinks: 'перенесено связей',
  movedIdentities: 'перенесено каналов',
  archivedVersions: 'архивировано версий',
  repointedSchedules: 'переключено расписаний',
  reusedPerson: 'персона переиспользована',
  ip: 'адрес',
  where: 'где',
  stage: 'этап',
  event: 'событие',
  entityType: 'тип записи',
  id: 'идентификатор',
};

/** Значения-константы, которые иначе уехали бы в интерфейс латиницей. */
const AUDIT_VALUE_LABEL: Record<string, string> = {
  LOCAL: 'локальный вход',
  VIBECODE: 'VibeCode',
  BUTTON: 'кнопка',
  OWNER_ID: 'владелец группы',
  EXPLICIT_FIELD: 'явное поле',
  EXPIRED: 'срок истёк',
  NOT_FOUND: 'не найдено',
  PIN_MISMATCH: 'неверный PIN',
  ATTEMPTS_EXHAUSTED: 'попытки исчерпаны',
  NO_MANAGER: 'менеджер не назначен',
  BITRIX_PROJECT_MISSING: 'проекта нет в Bitrix24',
  APPLIED: 'операция применилась',
  NOT_APPLIED: 'операция не применилась',
  true: 'да',
  false: 'нет',
};

export function auditFieldLabel(key: string): string {
  return AUDIT_FIELD_LABEL[key] ?? key;
}

export function auditValueLabel(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  const raw = String(value);
  return AUDIT_VALUE_LABEL[raw] ?? ROLE_LABEL_SOURCE[raw as keyof typeof ROLE_LABEL_SOURCE] ?? raw;
}
